import { useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** T278 row as GET /v1/audit/logs returns it. */
interface AuditRow {
  readonly logId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly actorIp: string | null;
  readonly eventTimestamp: string;
}

interface AuditResponse { readonly success?: boolean; readonly data?: AuditRow[]; readonly total?: number }

export function AuditSearchPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const [actorId, setActorId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (actorId) qs.set('actorId', actorId);
      if (entityType) qs.set('entityType', entityType);
      if (from) qs.set('fromDate', from);
      if (to) qs.set('toDate', to);
      const r = await apiRequest<AuditResponse>(`/v1/audit/logs?${qs.toString()}`, { token });
      const data = Array.isArray(r?.data) ? r.data : [];
      setRows(data);
      setTotal(typeof r?.total === 'number' ? r.total : data.length);
    } catch (err) { setError((err as Error).message); }
  }

  return (
    <main aria-label="audit-search">
      <h1>{t('audit.search.title')}</h1>
      <form onSubmit={onSearch} aria-label="audit-form">
        <RecordPicker<{ userId: string; displayName?: string; employeeCode?: string }>
          endpoint="/v1/users?limit=200"
          idField="userId"
          label={t('audit.search.actor')}
          testId="actor-id"
          value={actorId}
          optionLabel={(u) =>
            [u.employeeCode, u.displayName].filter(Boolean).join(' \u2014 ') || u.userId
          }
          onPick={setActorId}
          emptyText="No user on file"
        />
        <label htmlFor="a-entity">{t('audit.search.entityType')}</label>
        <input id="a-entity" aria-label="entity-type" value={entityType} onChange={(e) => setEntityType(e.target.value)} />
        <label htmlFor="a-from">{t('audit.search.from')}</label>
        <DateInput id="a-from" type="date" value={from} onValueChange={(nextValue) => setFrom(nextValue)} />
        <label htmlFor="a-to">{t('audit.search.to')}</label>
        <DateInput id="a-to" type="date" value={to} onValueChange={(nextValue) => setTo(nextValue)} />
        <button type="submit">{t('audit.search.search')}</button>
      </form>
      {error ? <p role="alert">{error}</p> : null}
      {rows && rows.length === 0 ? <p aria-label="no-audit-rows">{t('audit.search.empty')}</p> : null}
      {rows && rows.length > 0 && (
        <>
          <p aria-label="audit-total">{t('audit.search.total', { total: String(total) })}</p>
          <table aria-label="audit-table">
            <thead><tr>
              <th>{t('audit.search.col.when')}</th><th>{t('audit.search.col.event')}</th>
              <th>{t('audit.search.col.action')}</th><th>{t('audit.search.col.actor')}</th>
              <th>{t('audit.search.col.entity')}</th><th />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.logId} aria-label={`audit-row-${r.logId}`}>
                  <td>{at(r.eventTimestamp)}</td><td>{r.eventType}</td><td>{r.action}</td>
                  <td>{personLabel(r.actorName, r.actorCode) ?? '—'}</td><td>{r.entityType}</td>
                  <td><Link to={`/audit/${r.logId}`}>{t('audit.search.open')}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
